import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { L10n } from '../../../../ts/L10n/L10n';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { RenameAction } from '../../../../ts/services/RenameAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { RenameContentAction } from '../../../actions/spaces/RenameContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { CompositeSessionInfoSelectors } from '../../../selectors/SessionInfoSelectors';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function* handleRenameContent(action: RenameContentAction) {
  try {
    const ownerSelector = yield call(CompositeSessionInfoSelectors.currentUserIsOwnerSelector, action.content.owner);
    const isOwner: boolean = yield select(ownerSelector);

    //Need to replace datasource ID in the content to mitigate Datasource API expecting DataConnectionId
    let mutableContentArrayCopy: IContentInfo[] = [];
    mutableContentArrayCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, [action.content]);

    const result = yield call(openRenameDialog, mutableContentArrayCopy[0], isOwner);

    if (result && result.errors && result.errors.length > 0) {
      const localizedErrorMessage = yield call(getLocalizedErrorMessage, action.content);
      yield put(requestFailed(result.errors[0].message, localizedErrorMessage));
    } else {
      const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.content]);
      if (!maybeReloaded) {
        yield put(requestSpaceContents());
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getLocalizedErrorMessage(content: IContentInfo) {
  if (content.contentType === 'project') {
    return Messages.projectRenameError({ PROJECT_NAME: content.name });
  } else if (content.contentType === 'workbook') {
    return Messages.workbookRenameError({ WORKBOOK_NAME: content.name });
  } else if (content.contentType === 'flow') {
    return Messages.flowRenameError({ FLOW_NAME: content.name });
  } else if (content.contentType === 'schedule') {
    return Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(content.name) });
  } else if (content.contentType === 'metric') {
    return Messages.metricRenameError({ METRIC_NAME: content.name });
  } else if (content.contentType === 'group') {
    return Messages.groupRenameError({ GROUP_NAME: content.name });
  } else if (content.contentType === 'publishedConnection') {
    return Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: content.name });
  } else if (content.contentType === 'collection') {
    return Messages.collectionRenameError({ COLLECTION_NAME: content.name });
  } else if (content.contentType === 'datasource') {
    return Messages.datasourceRenameError({DATASOURCE_NAME: content.name});
  }
  return Messages.errorUnknown();
}

export function openRenameDialog(content: IContentInfo, isOwner: boolean): IPromise<IResult> {
  if (content.contentType === 'project') {
    return RenameAction.renameProject(content);
  } else if (content.contentType === 'workbook') {
    return RenameAction.renameWorkbook(content);
  } else if (content.contentType === 'flow') {
    return RenameAction.renameFlow(content as IContentInfo<'flow'>);
  } else if (content.contentType === 'schedule') {
    return RenameAction.renameSchedule(content);
  } else if (content.contentType === 'metric') {
    return RenameAction.renameMetric(content);
  } else if (content.contentType === 'group') {
    return RenameAction.renameGroup(content);
  } else if (content.contentType === 'publishedConnection') {
    return RenameAction.renamePublishedConnection(content);
  } else if (content.contentType === 'collection') {
    return RenameAction.renameCollection(content, isOwner);
  } else if (content.contentType === 'datasource') {
    return RenameAction.renameDatasource(content);
  }
}
